import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { ClipboardService } from "../clipboard.service";
import { DigitalCardService } from "../digital-card.service";
import { DigitalCardEntryDialogData } from "../digital-cards.interface";

const PREVIEW_EMPLOYEE_ROLES = new Set([
  "SUPER_ADMIN",
  "COMPANY_MANAGER",
  "GROUP_MANAGER",
  "RESELLER",
]);

@Component({
  selector: "app-digital-card-entry-dialog",
  templateUrl: "./digital-card-entry-dialog.component.html",
  styleUrls: ["./digital-card-entry-dialog.component.scss"],
  standalone: false,
})
export class DigitalCardEntryDialogComponent {
  entryUrl: string | null = null;
  loading = true;
  sending = false;
  sent = false;
  recipientEmail: string | null = null;
  private companyName: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<DigitalCardEntryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DigitalCardEntryDialogData,
    private digitalCardService: DigitalCardService,
    private clipboard: ClipboardService,
    private auth: AuthService,
  ) {
    this.recipientEmail = this.data.email?.trim() || null;
    this.loadEntryLink();
    this.loadPreviewEmployeeData();
  }

  close(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dialogRef.close();
  }

  copyEntryLink(): void {
    if (!this.entryUrl) return;
    this.clipboard.copy(this.entryUrl, "digitalCards.linkCopied");
  }

  sendMessage(): void {
    if (this.sending) return;
    if (!this.recipientEmail) return;
    if (!this.entryUrl) return;
    this.sending = true;
    this.sent = false;
    const company = this.companyName?.trim() || "Your company";
    this.digitalCardService
      .sendEntryLinkMessage(
        this.data.profileId,
        {
          to: this.recipientEmail,
          name: this.data.name?.trim() ?? "",
          company,
        },
        this.data.mine === true,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sending = false;
          this.sent = true;
        },
        error: () => {
          this.sending = false;
        },
      });
  }

  private loadEntryLink(): void {
    this.loading = true;
    this.digitalCardService
      .getEntryLink(this.data.profileId, this.data.mine === true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.entryUrl = res?.entryUrl?.trim() ? res.entryUrl.trim() : null;
          this.loading = false;
        },
        error: () => {
          this.entryUrl = null;
          this.loading = false;
        },
      });
  }

  private loadPreviewEmployeeData(): void {
    const email = this.data.email?.trim();
    const canPreview = PREVIEW_EMPLOYEE_ROLES.has(
      (this.auth.appUser?.role ?? "").toString(),
    );
    const request$ =
      canPreview && email
        ? this.digitalCardService.getPreviewEmployee(email)
        : this.digitalCardService.getCurrentEmployee();
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const em = res?.employee;
        const recipientEmail = em?.email?.toString().trim();
        if (recipientEmail) {
          this.recipientEmail = recipientEmail;
        }
        const companyCandidate = em?.companyData?.name?.toString().trim();
        this.companyName = companyCandidate?.length ? companyCandidate : null;
      },
    });
  }
}
